/* eslint no-console:0 */
// This file is automatically compiled by Webpack, along with any other files
// present in this directory. You're encouraged to place your actual application logic in
// a relevant structure within app/javascript and only use these pack files to reference
// that code so it'll be compiled.
//
// To reference this file, add <%= javascript_pack_tag 'application' %> to the appropriate
// layout file, like app/views/layouts/application.html.erb

// Import packages
import 'core-js/stable'; // Include this polyfill globally to support js code inside of rails views, on older browsers
import 'regenerator-runtime/runtime.js'; // This is part of the js polyfill to support async functions globally
import jQuery from 'jquery';
import List from 'list.js';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';

// Make jQuery available globally BEFORE importing any other files that use it
window.$ = window.jQuery = jQuery;

// Give webpacker an entry to the scss through this file
import './stylesheets.scss';

// Show content once everything is loaded
function showContent() {
  document.body.classList.add('loaded');
}

// Wait for both DOM and all resources to be ready
if (document.readyState === 'complete') {
  document.body.classList.add('loaded');
} else {
  window.addEventListener('load', showContent);
}

// Make other utilities available globally
window.List = List;
window.debounce = debounce; // Expose lodash debounce on the window so rails views can use it
window.PropTypes = PropTypes; // Make PropTypes available globally for React components

// Set up global environment for older libraries
window.exports = window.exports || {};
window.module = window.module || { exports: {} };

// Comprehensive polyfill for Node.js utils (needed by elasticsearch client)
const utilsPolyfill = {
  inherits: function(ctor, superCtor) {
    ctor.super_ = superCtor;
    ctor.prototype = Object.create(superCtor.prototype, {
      constructor: {
        value: ctor,
        enumerable: false,
        writable: true,
        configurable: true
      }
    });
  }
};

// Make utils available in multiple ways for different module systems
window.util = window.util || utilsPolyfill;
window.utils = window.utils || utilsPolyfill;
if (typeof global !== 'undefined') {
  global.util = global.util || utilsPolyfill;
  global.utils = global.utils || utilsPolyfill;
}

// For CommonJS/require style access
if (typeof require !== 'undefined') {
  try {
    require.cache = require.cache || {};
    require.cache['util'] = { exports: utilsPolyfill };
    require.cache['utils'] = { exports: utilsPolyfill };
  } catch (e) {
    // Ignore if require is not available
  }
}

// Load jQuery plugins and vendor scripts dynamically after jQuery is available
Promise.all([
  import('multiple-select'),
  import('select2'),
  import('../../../vendor/assets/javascripts/active_admin.js'),
  import('../../../vendor/assets/javascripts/bootstrap.js'),
  import('../../../vendor/assets/javascripts/bootstrap-datepicker.js'),
  import('../../../vendor/assets/javascripts/bootstrap-modal.js'),
  import('../../../vendor/assets/javascripts/crossfilter.min.js'),
  import('../../../vendor/assets/javascripts/jquery.placeholder.js'),
  import('../../../vendor/assets/javascripts/jquery-ujs.js'),
  import('../../../vendor/assets/javascripts/jquery.remotipart.js')
]).then(() => {
  // Wait for plugins to fully initialize and attach to jQuery
  setTimeout(() => {
    // Load bootstrap-multiselect using dynamic script loading to ensure jQuery is available
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/bootstrap-multiselect/0.9.15/js/bootstrap-multiselect.min.js';
    script.integrity = 'sha512-aFvi2oPv3NjnjQv1Y/hmKD7RNMendo4CZ2DwQqMWzoURKxcqAoktj0nNG4LU8m23+Ws9X5uVDD4OXLqpUVXD5Q==';
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      // Load legacy jQuery-dependent scripts after jQuery is available
      Promise.all([
        import('../src/javascripts/components/add_providers/add_providers.js'),
        import('../src/javascripts/components/checklist_templates/checklist_templates.js'),
        import('../src/javascripts/components/clients/clients.js'),
        import('../src/javascripts/components/elements/element_destroy_audit_comment.js'),
        import('../src/javascripts/components/home_address/home_address_audit_comment.js'),
        import('../src/javascripts/components/inbox/inbox-element.js'),
        import('../src/javascripts/components/locations/locations_audit_comment.js'),
        import('../src/javascripts/components/passwordValidator/password_validator.js'),
        import('../src/javascripts/components/practice_address/practice_address_audit_comment.js'),
        import('../src/javascripts/components/providers/provider_auditing.js'),
        import('../src/javascripts/components/side-nav/sideNav.js'),
        import('../src/javascripts/utils/ie_placeholder.js'),
        import('../src/javascripts/views/facilities.js'),
        import('../src/javascripts/views/primary_sources.jsx')
      ]).catch(err => {
        console.warn('Could not load legacy scripts:', err);
      });

      // Dispatch jQuery ready event
      document.dispatchEvent(new CustomEvent('jqueryReady'));

      // Initialize Bootstrap tooltips after everything is loaded
      if (typeof $.fn.tooltip !== 'undefined') {
        $('[data-toggle="tooltip"]').tooltip();
      }
    };
    script.onerror = () => {
      console.warn('Could not load bootstrap-multiselect from CDN');
    };
    document.head.appendChild(script);
  }, 100);
});

// Support js file names relative to the javasripts directory, while exluding the specs directory:
// Note: Vite handles static assets differently - images are imported directly where needed
// Exclude files that have browser compatibility issues and legacy files that need jQuery loaded first
const componentRequireContext = import.meta.glob([
  '../src/javascripts/**/*.{js,jsx}',
  '!../src/javascripts/**/specs/**',
  '!../src/javascripts/**/redux_containers/**',
  '!../src/javascripts/**/style_guide/**',
  '!../src/javascripts/**/utils/**',
  '!../src/javascripts/**/views/**',
  '!../src/javascripts/components/add_providers/**',
  '!../src/javascripts/components/checklist_templates/**',
  '!../src/javascripts/components/clients/**',
  '!../src/javascripts/components/elements/**',
  '!../src/javascripts/components/home_address/**',
  '!../src/javascripts/components/inbox/**',
  '!../src/javascripts/components/locations/**',
  '!../src/javascripts/components/passwordValidator/**',
  '!../src/javascripts/components/practice_address/**',
  '!../src/javascripts/components/providers/**',
  '!../src/javascripts/components/side-nav/**'
], { eager: true });

// Convert Vite's glob import format to the format expected by ReactRailsUJS
const convertedContext = (name) => {
  // Remove leading ./ if present
  const cleanName = name.replace(/^\.\//, '');

  // Try to find the module with .js or .jsx extension
  const possiblePaths = [
    `../src/javascripts/${cleanName}.js`,
    `../src/javascripts/${cleanName}.jsx`
  ];

  let matchingModule = null;
  for (const path of possiblePaths) {
    if (componentRequireContext[path]) {
      matchingModule = path;
      break;
    }
  }

  if (matchingModule) {
    const module = componentRequireContext[matchingModule];
    return module.default || module;
  }

  // Throw an error if component is not found, as react_ujs expects
  throw new Error(`Cannot find module: ${name}`);
};

convertedContext.keys = () => Object.keys(componentRequireContext).map(key => key.replace('../src/javascripts/', '').replace(/\.(js|jsx)$/, ''));

import ReactRailsUJS from "react_ujs";
ReactRailsUJS.useContext(convertedContext);
